import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, type Discipline } from '../api';
import { keys, useDisciplines, useMembers, usePerm, useProjectId } from '../data';
import { PageHead } from '../layout';
import { Btn, DataTable, ErrorBox, Field, Icon, Loading, Modal, NumberInput, ReasonDialog, Select, Tabs, useForm, useToast } from '../ui/kit';
import { HistoryModal } from '../ui/history';
import { fmtNum } from '../format';

export function DisciplinesPage() {
  const id = useProjectId(); const perm = usePerm(id); const toast = useToast(); const qc = useQueryClient();
  const [tab, setTab] = useState<'ativas' | 'lixeira'>('ativas');
  const q = useDisciplines(id, tab === 'lixeira'); const members = useMembers(id);
  const [edit, setEdit] = useState<Discipline | 'new' | null>(null);
  const editable = perm.can('disciplinas', 'edit');
  const name = (uid: string | null) => members.data?.find(m => m.userId === uid)?.name ?? '';
  const defaults = async () => {
    try { const r = await api.post<{ incluidas: number }>(`/api/projects/${id}/disciplines/defaults`); toast.ok(r.incluidas ? `${r.incluidas} disciplina(s) incluída(s).` : 'As disciplinas padrão já estavam cadastradas.'); await qc.invalidateQueries({ queryKey: ['project', id, 'disciplines'] }); }
    catch (e) { toast.err(e); }
  };
  const restore = async (d: Discipline) => {
    try { await api.post(`/api/projects/${id}/disciplines/${d.id}/restore`); toast.ok('Disciplina restaurada.'); await qc.invalidateQueries({ queryKey: ['project', id, 'disciplines'] }); }
    catch (e) { toast.err(e); }
  };
  return (
    <>
      <PageHead eyebrow="Projeto" title="Disciplinas" sub="Disciplinas de engenharia do projeto, com coordenador e horas previstas.">
        {editable && <button className="btn" onClick={defaults}>Incluir disciplinas padrão</button>}
        {editable && <button className="btn primary" onClick={() => setEdit('new')}><Icon name="plus" />Nova disciplina</button>}
      </PageHead>
      <Tabs value={tab} onChange={setTab} tabs={[{ value: 'ativas', label: 'Ativas' }, { value: 'lixeira', label: 'Lixeira' }]} />
      <div className="panel">
        {q.isLoading ? <Loading /> : q.error ? <div className="panel-body"><ErrorBox error={q.error} /></div> : (
          <DataTable rows={q.data ?? []} onRow={tab === 'ativas' ? d => setEdit(d) : undefined} search={d => `${d.code} ${d.name}`} initialSort="code"
            empty={tab === 'ativas' ? 'Nenhuma disciplina. Use "Incluir disciplinas padrão" para começar com a lista siderúrgica.' : 'A lixeira está vazia.'}
            cols={[
              { key: 'code', label: 'Sigla', sort: d => d.code, render: d => <span className="tag">{d.code}</span> },
              { key: 'name', label: 'Disciplina', sort: d => d.name },
              { key: 'coord', label: 'Coordenador', render: d => name(d.coordinatorId), sort: d => name(d.coordinatorId) },
              { key: 'hoursPlanned', label: 'Horas previstas', num: true, sort: d => d.hoursPlanned ?? -1, render: d => fmtNum(d.hoursPlanned) },
              ...(tab === 'lixeira' && perm.can('disciplinas', 'approve') ? [{ key: 'r', label: '', render: (d: Discipline) => <button className="btn sm" onClick={() => void restore(d)}>Restaurar</button> }] : [])
            ]} />
        )}
      </div>
      {edit && <DisciplineForm projectId={id} item={edit === 'new' ? undefined : edit} readOnly={!editable} onClose={() => setEdit(null)} />}
    </>
  );
}

function DisciplineForm({ projectId, item, readOnly, onClose }: { projectId: string; item?: Discipline; readOnly: boolean; onClose: () => void }) {
  const toast = useToast(); const qc = useQueryClient(); const members = useMembers(projectId);
  const init = useMemo(() => ({ code: item?.code ?? '', name: item?.name ?? '', coordinatorId: item?.coordinatorId ?? null, hoursPlanned: item?.hoursPlanned ?? null, scope: item?.scope ?? null }), [item]);
  const { v, set, changed } = useForm(init);
  const [busy, setBusy] = useState(false); const [del, setDel] = useState(false); const [hist, setHist] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['project', projectId, 'disciplines'] });
  const save = async () => {
    setBusy(true);
    try {
      if (item) { if (Object.keys(changed).length) await api.patch(`/api/projects/${projectId}/disciplines/${item.id}`, { ...changed, version: item.version }); }
      else await api.post(`/api/projects/${projectId}/disciplines`, v);
      toast.ok('Disciplina salva.'); await refresh(); onClose();
    } catch (e) { toast.err(e); } finally { setBusy(false); }
  };
  return (
    <>
      <Modal title={item ? `${item.code} · ${item.name}` : 'Nova disciplina'} onClose={onClose}
        foot={<>
          {item && <button className="btn ghost" onClick={() => setHist(true)}><Icon name="history" />Histórico</button>}
          {item && !readOnly && <button className="btn danger" onClick={() => setDel(true)}><Icon name="trash" />Excluir</button>}
          <span className="spacer" />
          <button className="btn" onClick={onClose}>{readOnly ? 'Fechar' : 'Cancelar'}</button>
          {!readOnly && <Btn className="btn primary" why={busy ? 'Aguarde: gravando.' : !v.code ? 'Preencha a sigla.' : v.name.length < 2 && 'Preencha o nome.'} onClick={save}>Salvar</Btn>}
        </>}>
        <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0 }}>
          <div className="form-grid">
            <Field label="Sigla" required><input className="input mono" value={v.code} onChange={e => set('code', e.target.value.toUpperCase())} /></Field>
            <Field label="Nome" required><input className="input" value={v.name} onChange={e => set('name', e.target.value)} /></Field>
            <Field label="Coordenador" help="Precisa participar da equipe do projeto."><Select value={v.coordinatorId} onChange={x => set('coordinatorId', x)} empty="Não definido" options={(members.data ?? []).filter(m => !m.revokedAt).map(m => ({ value: m.userId, label: m.name }))} /></Field>
            <Field label="Horas previstas"><NumberInput value={v.hoursPlanned} onChange={x => set('hoursPlanned', x)} /></Field>
            <Field label="Escopo da disciplina" full><textarea className="input" value={v.scope ?? ''} onChange={e => set('scope', e.target.value || null)} /></Field>
          </div>
        </fieldset>
      </Modal>
      {del && item && <ReasonDialog title={`Excluir ${item.code}`} confirm="Enviar para a lixeira" danger onClose={() => setDel(false)}
        onConfirm={async reason => { await api.del(`/api/projects/${projectId}/disciplines/${item.id}`, { reason }); toast.ok('Disciplina enviada para a lixeira.'); await refresh(); onClose(); }}>
        <p className="muted">A disciplina vai para a lixeira e pode ser restaurada pelo gerente.</p>
      </ReasonDialog>}
      {hist && item && <HistoryModal projectId={projectId} recordId={item.id} title={item.code} onClose={() => setHist(false)} />}
    </>
  );
}
